import { Box, Button, Grid, IconButton, Paper } from "@material-ui/core";
import { Add, Close } from "@material-ui/icons";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { getAllRole } from "../../../../api/roleApi";
import { uploadImageFile } from "../../../../api/uploadApi";
import { deleteUserById, getAllUser, registerSystem, updateUserById } from "../../../../api/userApi";
import { encode } from "../../../../utils/crypt";
import { swalCondition } from "../../../../utils/swalHelper";
import FormUser from "./components/FormUser";
import { initailUserFormValues, initialTableOptions, tableColumns } from "./helpers/userManagementOption";

function UserManagement() {
    // ref
    const controllerRef = useRef(null);
    const tableRef = useRef(null);

    // global redux state
    const { userRedux, thisMenuPermission } = useSelector(state => {
        const { user } = state?.user;
        const { menuPermission } = state?.menu;
        return {
            userRedux: user,
            thisMenuPermission: menuPermission?.find(menu => menu?.path === window.location.pathname)?.permission,
        };
    });

    // componnent state
    const [isOpenModalAdd, setIsOpenModalAdd] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [tableOptions, setTableOptions] = useState(initialTableOptions);
    const [roles, setRoles] = useState([]);
    const [initailFormValues, setInitailFormValues] = useState(initailUserFormValues);

    // fetch data
    const fetchData = useCallback(async query => {
        try {
            const queryParams = {
                page: query?.page + 1 || 1,
                limit: query?.pageSize || 10,
                search: query?.search || "",
                order: query?.orderBy?.field || "id",
                direction: query?.orderBy?.field ? query?.orderDirection : "desc",
            };
            const res = await getAllUser(queryParams, controllerRef.current?.signal);
            if (res) {
                setTableOptions(currentState => ({
                    ...currentState,
                    currentPage: query?.page,
                    pageSize: query?.pageSize || 10,
                    totalData: res?.pagination?.totalData || 0,
                    order: query?.orderBy?.field || "id",
                    direction: query?.orderBy?.field ? query?.orderDirection : "desc",
                }));
                return {
                    data: res?.data || [],
                    page: query?.page || 0,
                    totalCount: res?.pagination?.totalData || 0,
                };
            }
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        } catch (error) {
            console.dir(error);
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }
    }, []);

    // set formik value
    const setUserFormValues = useCallback(user => {
        setInitailFormValues({
            id: user?.id,
            fullname: user?.fullname || "",
            roleId: user?.roleId || 0,
            phoneNumber: user?.phoneNumber || "",
            email: user?.email || "",
            username: user?.username || "",
            password: user?.password,
            images: [],
            imagePath: user?.imagePath,
            Role: user?.Role || {},
            isAdmin: user?.isAdmin || false,
        });
    }, []);

    // clear formik value
    const clearUserFormValues = useCallback(() => {
        setInitailFormValues(initailUserFormValues);
    }, []);

    // open modal create form
    const handleOpenCreateUserForm = useCallback(() => {
        clearUserFormValues();
        setIsCreateMode(true);
        setIsOpenModalAdd(true);
    }, [clearUserFormValues]);

    // open modal edit form
    const handleOpenEditUserForm = useCallback(
        user => {
            setUserFormValues(user);
            setIsCreateMode(false);
            setIsOpenModalAdd(true);
        },
        [setUserFormValues],
    );

    // close modal
    const handleCloseForm = useCallback(() => {
        clearUserFormValues();
        setIsCreateMode(false);
        setIsOpenModalAdd(false);
    }, [clearUserFormValues]);

    // create logic
    const handleCreateUser = useCallback(async user => {
        try {
            // encode password
            const encodedPassword = encode(user?.password);

            // upload image
            let imagePath = "";
            if (user?.images?.length) {
                const formData = new FormData();
                formData.append("image", user?.images[0]?.src);
                imagePath = await uploadImageFile(formData, controllerRef.current?.signal);
            }

            delete user?.images;

            // create user
            const isCreateUser = await registerSystem(
                { ...user, password: encodedPassword, imagePath },
                controllerRef.current?.signal,
            );
            if (isCreateUser) {
                tableRef.current.onQueryChange();
                setIsOpenModalAdd(false);
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // update logic
    const handleUpdateUser = useCallback(async user => {
        try {
            // encode password
            let encodedPassword;
            if (user?.password) {
                encodedPassword = await encode(user?.password);
            }

            // upload image
            let imagePath = user?.imagePath;
            if (user?.images?.length) {
                const formData = new FormData();
                formData.append("image", user?.images[0]?.src);
                imagePath = await uploadImageFile(formData, controllerRef.current?.signal);
            }

            delete user?.images;

            // update user
            const isUpdateUser = await updateUserById(
                user?.id,
                { ...user, password: encodedPassword, imagePath },
                controllerRef.current?.signal,
            );
            if (isUpdateUser) {
                tableRef.current.onQueryChange();
                setIsOpenModalAdd(false);
            }
        } catch (error) {
            console.dir(error);
        }
    }, []);

    // handle function Delete Data Table
    const handleDeleteUser = useCallback(async user => {
        const isConfirm = await swalCondition('<div className="h3 t-text-primary">Are you delete?</div>', "", {
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "YES",
            cancelButtonText: "NO",
        });
        if (isConfirm) {
            const isDeleteUser = await deleteUserById(user?.id, controllerRef.current?.signal);
            if (isDeleteUser) {
                tableRef.current.onQueryChange();
                setIsOpenModalAdd(false);
            }
        }
    }, []);

    // run only first render
    useEffect(() => {
        controllerRef.current = new AbortController();
        const init = async () => {
            const _roles = await getAllRole(controllerRef.current?.signal);
            if (_roles) {
                setRoles(_roles);
            }
        };
        init();
        // to avoid memory leaked.
        return () => {
            controllerRef.current.abort();
        };
    }, []);

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={7} className="mb-5 mb-lg-0">
                    <h1 className="t-text-primary text-center text-xl-left">User Management</h1>
                </Grid>
                {thisMenuPermission?.create && (
                    <Grid item xs={12} lg={5} className="mb-5 mb-lg-0">
                        <Grid container spacing={3} justify={"flex-end"}>
                            <Grid item xs={12} lg={5}>
                                <Button onClick={handleOpenCreateUserForm} className="btn t-btn-primary w-100">
                                    <Add className="mr-2" /> Create User
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                {/* Data Table material */}
                <Grid item xs={12}>
                    <Paper>
                        <Box className="t-table-custom p-lg-10 p-5">
                            <MaterialTable
                                tableRef={tableRef}
                                components={{ Container: Box }}
                                title=""
                                data={fetchData}
                                columns={tableColumns}
                                options={tableOptions}
                                actions={[
                                    {
                                        icon: "edit",
                                        iconProps: { style: { fontSize: 18 }, className: "action-edit" },
                                        tooltip: "Edit",
                                        onClick: (event, rowData) => handleOpenEditUserForm(rowData),
                                        hidden: !thisMenuPermission?.edit,
                                    },
                                    rowData => ({
                                        icon: "delete",
                                        iconProps: { style: { fontSize: 18 }, className: "action-delete" },
                                        tooltip: "Delete",
                                        onClick: (event, rowData) => handleDeleteUser(rowData),
                                        hidden: rowData?.id === userRedux?.id || rowData?.isAdmin || !thisMenuPermission?.delete,
                                    }),
                                ]}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Modal Form */}
                <Grid item xs={12}>
                    {/* Modal Create Broad */}
                    <Modal show={isOpenModalAdd} size="lg" scrollable={true} onHide={handleCloseForm}>
                        <Modal.Header>
                            <Modal.Title>{isCreateMode ? "Create User" : "Update User"}</Modal.Title>
                            <IconButton onClick={handleCloseForm}>
                                <Close />
                            </IconButton>
                        </Modal.Header>
                        <Modal.Body>
                            <FormUser
                                onSubmit={isCreateMode ? handleCreateUser : handleUpdateUser}
                                onClose={handleCloseForm}
                                isCreateMode={isCreateMode}
                                roles={roles}
                                initailFormValues={initailFormValues}
                            />
                        </Modal.Body>
                    </Modal>
                </Grid>
            </Grid>
        </div>
    );
}

export default UserManagement;
